import { 
  SparklesIcon, 
  CurrencyDollarIcon,
  CommandLineIcon,
  CpuChipIcon,
  DocumentTextIcon,
  ClockIcon,
  PhotoIcon,
} from '@heroicons/react/24/outline';
import DashboardCharts from '../components/DashboardCharts';
import WelcomeSection from '../components/WelcomeSection';
import ModelPerformance from '../components/ModelPerformance';

const stats = [
  { 
    name: 'Tokens Used Today', 
    value: '1.2M', 
    change: '+15.2%', 
    icon: SparklesIcon, 
    trend: 'up' 
  },
  { 
    name: 'Images Generated', 
    value: '847', 
    change: '+23.5%', 
    icon: PhotoIcon, 
    trend: 'up' 
  },
  { 
    name: 'Avg Response Time', 
    value: '520ms', 
    change: '-50ms', 
    icon: ClockIcon, 
    trend: 'down' 
  },
  { 
    name: 'Cost Today', 
    value: '$124.50', 
    change: '+$15.20', 
    icon: CurrencyDollarIcon, 
    trend: 'up',
    breakdown: {
      text: '$84.30',
      images: '$40.20',
    }
  },
];

const activities = [
  {
    id: 1,
    type: 'Image Generation',
    description: 'Generated 50 high-resolution images with DALL-E 3',
    time: '1 hour ago',
    icon: PhotoIcon,
  },
  {
    id: 2,
    type: 'Model Switch',
    description: 'Switched from GPT-4 to GPT-3.5-turbo for /chat endpoint',
    time: '2 hours ago',
    icon: CpuChipIcon,
  },
  {
    id: 3,
    type: 'High Token Usage',
    description: 'Prompt optimization recommended for /completion endpoint',
    time: '4 hours ago',
    icon: SparklesIcon,
  },
  {
    id: 4,
    type: 'New Prompt Template',
    description: 'Added template "Customer Support Assistant v2"',
    time: '6 hours ago',
    icon: DocumentTextIcon,
  },
  {
    id: 5,
    type: 'Response Error',
    description: 'Increased rate of context length errors on /chat endpoint',
    time: '8 hours ago',
    icon: CommandLineIcon,
  },
];

export default function Dashboard() {
  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <WelcomeSection />

      {/* Stats */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05]">
          <h2 className="text-lg font-medium text-white">
            Performance Metrics
          </h2>
        </div>
        
        <div className="p-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            const isPositive = stat.trend === 'up';
            return (
              <div key={stat.name} className="glass-panel-dark group">
                <div className="px-6 py-5">
                  <div className="flex items-center justify-between">
                    <div className="bg-white/[0.05] p-3 rounded-lg group-hover:bg-white/[0.08] transition-colors duration-300">
                      <Icon className="h-6 w-6 text-white/90" />
                    </div>
                    <span className={`text-sm font-medium px-2.5 py-1 rounded-full ${
                      isPositive 
                        ? 'text-emerald-300 bg-emerald-500/10' 
                        : 'text-rose-300 bg-rose-500/10'
                    }`}>
                      {stat.change}
                    </span>
                  </div>
                  <div className="mt-6">
                    <div className="text-3xl font-semibold text-white">
                      {stat.value}
                    </div>
                    <div className="text-sm font-medium text-white/50 mt-1">
                      {stat.name}
                    </div>
                    {stat.breakdown && (
                      <div className="mt-2 space-y-1">
                        <div className="text-xs text-white/40 flex justify-between">
                          <span>Text Generation:</span>
                          <span>{stat.breakdown.text}</span>
                        </div>
                        <div className="text-xs text-white/40 flex justify-between">
                          <span>Image Generation:</span>
                          <span>{stat.breakdown.images}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Model Performance */}
      <ModelPerformance />

      {/* Usage Charts */}
      <DashboardCharts />

      {/* Recent Activity */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05] flex items-center justify-between">
          <h2 className="text-lg font-medium text-white">
            Recent Activity
          </h2>
          <button className="btn-secondary text-sm">View All</button>
        </div>
        <div>
          {activities.map((activity, i) => {
            const Icon = activity.icon;
            return (
              <div 
                key={activity.id} 
                className={`hover:bg-white/[0.02] transition-colors duration-200
                  ${i !== 0 ? 'border-t border-white/[0.03]' : ''}`}
              >
                <div className="px-8 py-4">
                  <div className="flex items-center space-x-4">
                    <div className="bg-white/[0.05] p-2 rounded-lg">
                      <Icon className="h-5 w-5 text-white/90" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">
                        {activity.type}
                      </p>
                      <p className="text-sm text-white/50 truncate">
                        {activity.description}
                      </p>
                    </div>
                    <div className="text-sm text-white/40">
                      {activity.time}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
